import { useEffect, useState, useCallback } from 'react';

export interface AccessibilityPreferences {
  highContrast: boolean;
  reducedMotion: boolean;
  fontSize: 'small' | 'medium' | 'large';
  keyboardNavigation: boolean;
}

export interface AccessibilityState {
  preferences: AccessibilityPreferences;
  focusVisible: boolean;
  announcements: string[];
}

export const useAccessibility = () => {
  const [state, setState] = useState<AccessibilityState>({
    preferences: {
      highContrast: false,
      reducedMotion: false,
      fontSize: 'medium',
      keyboardNavigation: false,
    },
    focusVisible: false,
    announcements: [],
  });

  // Detect system preferences
  useEffect(() => {
    const detectSystemPreferences = () => {
      const highContrast = window.matchMedia?.('(prefers-contrast: high)')?.matches || false;
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches || false;
      
      setState(prev => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          highContrast,
          reducedMotion,
        },
      }));
    };

    detectSystemPreferences();

    // Listen for changes in system preferences
    const highContrastQuery = window.matchMedia('(prefers-contrast: high)');
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const handleHighContrastChange = (e: MediaQueryListEvent) => {
      setState(prev => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          highContrast: e.matches,
        },
      }));
    };

    const handleReducedMotionChange = (e: MediaQueryListEvent) => {
      setState(prev => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          reducedMotion: e.matches,
        },
      }));
    };

    highContrastQuery.addEventListener('change', handleHighContrastChange);
    reducedMotionQuery.addEventListener('change', handleReducedMotionChange);

    return () => {
      highContrastQuery.removeEventListener('change', handleHighContrastChange);
      reducedMotionQuery.removeEventListener('change', handleReducedMotionChange);
    };
  }, []);

  // Detect keyboard navigation
  useEffect(() => {
    let isKeyboardUser = false;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        isKeyboardUser = true;
        setState(prev => ({
          ...prev,
          preferences: {
            ...prev.preferences,
            keyboardNavigation: true,
          },
          focusVisible: true,
        }));
      }
    };

    const handleMouseDown = () => {
      if (isKeyboardUser) {
        setState(prev => ({
          ...prev,
          focusVisible: false,
        }));
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  // Load saved preferences
  useEffect(() => {
    try {
      const savedPreferences = localStorage.getItem('accessibility-preferences');
      if (savedPreferences) {
        const parsed = JSON.parse(savedPreferences);
        setState(prev => ({
          ...prev,
          preferences: {
            ...prev.preferences,
            ...parsed,
          },
        }));
      }
    } catch (error) {
      console.error('Failed to load accessibility preferences:', error);
    }
  }, []);

  // Save preferences when they change
  useEffect(() => {
    localStorage.setItem('accessibility-preferences', JSON.stringify(state.preferences));
  }, [state.preferences]);

  // Apply accessibility classes to document
  useEffect(() => {
    const { highContrast, reducedMotion, fontSize, keyboardNavigation } = state.preferences;
    
    document.documentElement.classList.toggle('high-contrast', highContrast);
    document.documentElement.classList.toggle('reduced-motion', reducedMotion);
    document.documentElement.classList.toggle('keyboard-navigation', keyboardNavigation);
    document.documentElement.classList.toggle('focus-visible', state.focusVisible);
    
    // Apply font size
    document.documentElement.classList.remove('font-small', 'font-medium', 'font-large');
    document.documentElement.classList.add(`font-${fontSize}`);
  }, [state.preferences, state.focusVisible]);

  const updatePreferences = useCallback((updates: Partial<AccessibilityPreferences>) => {
    setState(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        ...updates,
      },
    }));
  }, []);

  const announce = useCallback((message: string) => {
    setState(prev => ({
      ...prev,
      announcements: [...prev.announcements, message],
    }));

    // Clear announcement after a delay
    setTimeout(() => {
      setState(prev => ({
        ...prev,
        announcements: prev.announcements.filter(a => a !== message),
      }));
    }, 1000);
  }, []);

  const clearAnnouncements = useCallback(() => {
    setState(prev => ({
      ...prev,
      announcements: [],
    }));
  }, []);

  return {
    ...state,
    updatePreferences,
    announce,
    clearAnnouncements,
  };
};

// Hook for keyboard navigation
export const useKeyboardNavigation = (
  onEnter?: () => void,
  onEscape?: () => void,
  onArrowKeys?: (direction: 'up' | 'down' | 'left' | 'right') => void
) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Enter':
        case ' ':
          if (onEnter) {
            e.preventDefault();
            onEnter();
          }
          break;
        case 'Escape':
          if (onEscape) {
            e.preventDefault();
            onEscape();
          }
          break;
        case 'ArrowUp':
          if (onArrowKeys) {
            e.preventDefault();
            onArrowKeys('up');
          }
          break;
        case 'ArrowDown':
          if (onArrowKeys) {
            e.preventDefault();
            onArrowKeys('down');
          }
          break;
        case 'ArrowLeft':
          if (onArrowKeys) {
            e.preventDefault();
            onArrowKeys('left');
          }
          break;
        case 'ArrowRight':
          if (onArrowKeys) {
            e.preventDefault();
            onArrowKeys('right');
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onEnter, onEscape, onArrowKeys]);
};

// Hook for focus management
export const useFocusManagement = () => {
  const focusElement = useCallback((selector: string) => {
    const element = document.querySelector(selector) as HTMLElement;
    if (element) {
      element.focus();
    }
  }, []);

  const trapFocus = useCallback((containerSelector: string) => {
    const container = document.querySelector(containerSelector) as HTMLElement;
    if (!container) return () => {};

    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    ) as NodeListOf<HTMLElement>;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    container.addEventListener('keydown', handleKeyDown);
    firstElement?.focus();

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return {
    focusElement,
    trapFocus,
  };
};